import React from 'react';
import { Alert, Box, Button, Chip, Divider, MenuItem, Stack, Switch, TextField, Typography } from '@mui/material';
import {
    CommentDto,
    CommentDtoCommentType,
    CommentDtoLevel,
    IConcertAdminDto,
    IConcertSeriesAdminDto,
    ILocationAdminDto,
    LinkDto,
} from '../../clients/services';
import { DateTime } from 'luxon';
import { SeriesContentEditor } from './SeriesContentEditor';
import { ImageDropZone } from '../AdminShared/ImageDropZone';

interface SeriesDetailPanelProps {
    series?: IConcertSeriesAdminDto;
    concerts: IConcertAdminDto[];
    locations: ILocationAdminDto[];
    mode: 'series' | 'concert';
    onSeriesChange: (updated: IConcertSeriesAdminDto) => void;
    onSeriesVisibilityChange: (updated: IConcertSeriesAdminDto, successMessage: string) => Promise<void>;
    onSaveSeries: () => Promise<void>;
    onAddConcert: () => Promise<void>;
    onSelectConcert: (concertId: number) => void;
    onSeriesBannerDropped: (file: File) => Promise<void>;
    onSeriesPictureDropped: (file: File) => Promise<void>;
    onSeriesBannerPick: () => void;
    onSeriesPicturePick: () => void;
    onClearSeriesBanner: () => Promise<void>;
    onClearSeriesPicture: () => Promise<void>;
    onParagraphPictureDropped: (index: number, file: File) => Promise<void>;
    onParagraphPicturePick: (index: number) => void;
    onClearParagraphPicture: (index: number) => void;
}

const commentLevelOptions = [
    CommentDtoLevel.Info,
    CommentDtoLevel.Warning,
    CommentDtoLevel.Error,
    CommentDtoLevel.Success,
];

const commentTypeOptions = [
    CommentDtoCommentType.Concert,
    CommentDtoCommentType.TicketSale,
    CommentDtoCommentType.InPast,
    CommentDtoCommentType.Canceled,
    CommentDtoCommentType.NoDate,
    CommentDtoCommentType.Postponed,
    CommentDtoCommentType.Alert,
];

const formatDate = (value?: any): string => {
    if (!value || typeof value.toFormat !== 'function') {
        return '-';
    }
    return value.toFormat('dd.MM.yyyy');
};

const formatConcertTimes = (concert: IConcertAdminDto): string => {
    const doors = concert.doors?.trim() || '-';
    const start = concert.start?.trim() || '-';
    return `Doors: ${doors} | Konzertbeginn: ${start}`;
};

const compactAdminFormSx = {
    '& .MuiTextField-root .MuiInputBase-input': {
        fontSize: '0.86rem',
        paddingTop: '8px',
        paddingBottom: '8px',
    },
    '& .MuiInputLabel-root': {
        fontSize: '0.82rem',
    },
    '& .MuiFormHelperText-root': {
        fontSize: '0.72rem',
    },
    '& .MuiButton-root': {
        fontSize: '0.8rem',
        paddingTop: '4px',
        paddingBottom: '4px',
        minHeight: 30,
    },
    '& .MuiTypography-h5': {
        fontSize: '1.2rem',
    },
    '& .MuiTypography-h6': {
        fontSize: '1rem',
    },
};

export const SeriesDetailPanel: React.FC<SeriesDetailPanelProps> = ({
    series,
    concerts,
    locations,
    mode,
    onSeriesChange,
    onSeriesVisibilityChange,
    onSaveSeries,
    onAddConcert,
    onSelectConcert,
    onSeriesBannerDropped,
    onSeriesPictureDropped,
    onSeriesBannerPick,
    onSeriesPicturePick,
    onClearSeriesBanner,
    onClearSeriesPicture,
    onParagraphPictureDropped,
    onParagraphPicturePick,
    onClearParagraphPicture,
}) => {
    const locationById = new Map<number, ILocationAdminDto>(locations.map((item) => [item.id, item]));

    const concertDates = concerts
        .map((item) => item.date)
        .filter((item): item is DateTime => !!item && typeof item.toMillis === 'function')
        .sort((a, b) => a.toMillis() - b.toMillis());

    const derivedSeriesStart = concertDates[0] ?? null;
    const derivedSeriesEnd = concertDates.length ? concertDates[concertDates.length - 1] : null;

    const formatLocation = (locationId?: number | null): string => {
        if (!locationId) {
            return 'Konzertlokal nicht gesetzt';
        }

        const location = locationById.get(locationId);
        if (!location) {
            return 'Konzertlokal nicht gesetzt';
        }

        if (location.city) {
            return `${location.name} (${location.city})`;
        }

        return location.name || 'Konzertlokal nicht gesetzt';
    };

    if (!series) {
        return (
            <Box sx={{ p: 2 }}>
                <Alert severity="info">Bitte eine Konzertserie auswaehlen.</Alert>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                p: 1.25,
                height: '100%',
                overflow: 'auto',
                backgroundColor: '#ffffff',
                color: '#1f1f1f',
                ...compactAdminFormSx,
            }}
        >
            <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                <Typography variant="h5">Serie: {series.title}</Typography>
            </Stack>

            <Stack spacing={1.2}>
                <Stack direction="row" spacing={2}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Switch
                            checked={series.hide ?? false}
                            onChange={async (event) => {
                                const isHidden = event.target.checked;
                                const updated = { ...series, hide: isHidden };
                                onSeriesChange(updated);
                                await onSeriesVisibilityChange(
                                    updated,
                                    isHidden ? 'Serie versteckt.' : 'Serie publiziert.',
                                );
                            }}
                        />
                        <Typography>Serie versteckt</Typography>
                    </Stack>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Switch
                            checked={series.hideConcerts ?? false}
                            onChange={async (event) => {
                                const hideConcerts = event.target.checked;
                                const updated = { ...series, hideConcerts };
                                onSeriesChange(updated);
                                await onSeriesVisibilityChange(updated, 'Preview-Einstellung gespeichert.');
                            }}
                        />
                        <Typography>Konzerte in Preview ausblenden</Typography>
                    </Stack>
                </Stack>

                <Stack direction="row" spacing={1}>
                    <Chip
                        label={series.hide ? 'Versteckt' : 'Publiziert'}
                        color={series.hide ? 'warning' : 'success'}
                    />
                </Stack>

                <TextField
                    label="Titel"
                    fullWidth
                    value={series.title || ''}
                    onChange={(event) => onSeriesChange({ ...series, title: event.target.value })}
                />

                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
                    <TextField
                        label="Serienstart"
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        value={derivedSeriesStart ? derivedSeriesStart.toFormat('yyyy-LL-dd') : ''}
                        disabled
                    />
                    <TextField
                        label="Serienende"
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        value={derivedSeriesEnd ? derivedSeriesEnd.toFormat('yyyy-LL-dd') : ''}
                        disabled
                    />
                </Stack>
            </Stack>

            <Stack spacing={1} sx={{ mt: 2 }}>
                <ImageDropZone
                    label="Banner"
                    imageUrl={series.banner?.url || undefined}
                    imageTitle={series.banner?.title || undefined}
                    onFileDropped={onSeriesBannerDropped}
                    onOpenMediaPicker={onSeriesBannerPick}
                    onClear={onClearSeriesBanner}
                    previewMaxHeight={420}
                    previewObjectFit="contain"
                />
            </Stack>

            <SeriesContentEditor
                series={series}
                seriesPicture={series.picture ?? null}
                onSeriesPictureDropped={onSeriesPictureDropped}
                onSeriesPicturePick={onSeriesPicturePick}
                onClearSeriesPicture={onClearSeriesPicture}
                onParagraphPictureDropped={onParagraphPictureDropped}
                onParagraphPicturePick={onParagraphPicturePick}
                onClearParagraphPicture={onClearParagraphPicture}
                onChange={onSeriesChange}
            />

            <Divider sx={{ my: 2 }} />

            <TextField
                label="Ticketverkauf Start"
                type="date"
                InputLabelProps={{ shrink: true }}
                fullWidth
                value={series.ticketSaleStart ? series.ticketSaleStart.toFormat('yyyy-LL-dd') : ''}
                onChange={(event) =>
                    onSeriesChange({
                        ...series,
                        ticketSaleStart: event.target.value ? DateTime.fromISO(event.target.value) : null,
                    })
                }
            />

            <Divider sx={{ my: 2 }} />
            <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                <Typography variant="h6">Konzerte dieser Serie</Typography>
                <Button variant="contained" size="small" onClick={onAddConcert}>
                    Konzert hinzufuegen
                </Button>
            </Stack>
            <Stack spacing={1}>
                {concerts.map((concert) => (
                    <Box
                        key={concert.id}
                        sx={{
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                            p: 1,
                            cursor: 'pointer',
                            backgroundColor: concert.hide ? 'warning.50' : 'background.paper',
                        }}
                        onClick={() => onSelectConcert(concert.id)}
                    >
                        <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center">
                            <Typography fontWeight={700}>{formatDate(concert.date)}</Typography>
                            {concert.hide && <Chip size="small" color="warning" label="VERSTECKT" />}
                        </Stack>
                        <Typography>{formatLocation(concert.locationId)}</Typography>
                        <Typography variant="body2" color="text.secondary">
                            {formatConcertTimes(concert)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {concert.title || 'Ohne Titel'}
                        </Typography>
                    </Box>
                ))}
            </Stack>

            <Divider sx={{ my: 2 }} />

            <Typography variant="h6" sx={{ mb: 1 }}>
                Links
            </Typography>
            <Stack spacing={1}>
                {(series.links ?? []).map((link, index) => (
                    <Stack key={`${link.url}-${index}`} direction="row" spacing={1}>
                        <TextField
                            label="Titel"
                            fullWidth
                            value={link.title ?? ''}
                            onChange={(event) => {
                                const nextLinks = [...(series.links ?? [])];
                                nextLinks[index] = new LinkDto({ ...nextLinks[index], title: event.target.value });
                                onSeriesChange({ ...series, links: nextLinks });
                            }}
                        />
                        <TextField
                            label="URL"
                            fullWidth
                            value={link.url ?? ''}
                            onChange={(event) => {
                                const nextLinks = [...(series.links ?? [])];
                                nextLinks[index] = new LinkDto({ ...nextLinks[index], url: event.target.value });
                                onSeriesChange({ ...series, links: nextLinks });
                            }}
                        />
                        <Button
                            color="error"
                            onClick={() => {
                                const nextLinks = [...(series.links ?? [])];
                                nextLinks.splice(index, 1);
                                onSeriesChange({ ...series, links: nextLinks });
                            }}
                        >
                            X
                        </Button>
                    </Stack>
                ))}
                <Button
                    variant="outlined"
                    onClick={() =>
                        onSeriesChange({
                            ...series,
                            links: [...(series.links ?? []), new LinkDto({ title: '', url: '' })],
                        })
                    }
                >
                    Link hinzufuegen
                </Button>
            </Stack>

            <Divider sx={{ my: 2 }} />

            <Typography variant="h6" sx={{ mb: 1 }}>
                Kommentare
            </Typography>
            <Stack spacing={1}>
                {(series.comments ?? []).map((comment, index) => (
                    <CardCommentEditor
                        key={`${comment.message}-${index}`}
                        index={index}
                        comment={comment}
                        onChange={(updated) => {
                            const next = [...(series.comments ?? [])];
                            next[index] = updated;
                            onSeriesChange({ ...series, comments: next });
                        }}
                        onRemove={() => {
                            const next = [...(series.comments ?? [])];
                            next.splice(index, 1);
                            onSeriesChange({ ...series, comments: next });
                        }}
                    />
                ))}
                <Button
                    variant="outlined"
                    onClick={() =>
                        onSeriesChange({
                            ...series,
                            comments: [
                                ...(series.comments ?? []),
                                new CommentDto({
                                    message: '',
                                    commentType: CommentDtoCommentType.Concert,
                                    level: CommentDtoLevel.Info,
                                }),
                            ],
                        })
                    }
                >
                    Kommentar hinzufuegen
                </Button>
            </Stack>

            <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                <Button variant="contained" onClick={onSaveSeries}>
                    Serie speichern
                </Button>
            </Stack>
        </Box>
    );
};

const CardCommentEditor = (props: {
    index: number;
    comment: CommentDto;
    onChange: (value: CommentDto) => void;
    onRemove: () => void;
}) => {
    const { comment } = props;
    return (
        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1 }}>
            <Stack spacing={1}>
                <Stack direction="row" spacing={1}>
                    <TextField
                        label="Titel"
                        fullWidth
                        value={comment.title ?? ''}
                        onChange={(event) => props.onChange(new CommentDto({ ...comment, title: event.target.value }))}
                    />
                    <Button color="error" onClick={props.onRemove}>
                        X
                    </Button>
                </Stack>
                <TextField
                    label="Nachricht"
                    fullWidth
                    multiline
                    minRows={2}
                    value={comment.message ?? ''}
                    onChange={(event) => props.onChange(new CommentDto({ ...comment, message: event.target.value }))}
                />
                <Stack direction="row" spacing={1}>
                    <TextField
                        label="CSS Klasse"
                        fullWidth
                        value={comment.className ?? ''}
                        onChange={(event) =>
                            props.onChange(new CommentDto({ ...comment, className: event.target.value }))
                        }
                    />
                    <TextField
                        select
                        label="Level"
                        fullWidth
                        value={comment.level ?? CommentDtoLevel.Info}
                        onChange={(event) =>
                            props.onChange(new CommentDto({ ...comment, level: event.target.value as CommentDtoLevel }))
                        }
                    >
                        {commentLevelOptions.map((option) => (
                            <MenuItem key={option} value={option}>
                                {option}
                            </MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        select
                        label="Typ"
                        fullWidth
                        value={comment.commentType ?? CommentDtoCommentType.Concert}
                        onChange={(event) =>
                            props.onChange(
                                new CommentDto({
                                    ...comment,
                                    commentType: event.target.value as CommentDtoCommentType,
                                }),
                            )
                        }
                    >
                        {commentTypeOptions.map((option) => (
                            <MenuItem key={option} value={option}>
                                {option}
                            </MenuItem>
                        ))}
                    </TextField>
                </Stack>
            </Stack>
        </Box>
    );
};
